// Wave 9 — useLiveStats hook.
//
// Subscribes to /api/ws/live-stats and exposes the latest counter snapshot.
// Reconnects with exponential backoff (1s → 2s → 4s … capped at 30s) so a
// transient gateway hiccup doesn't leave the dashboard frozen forever.
// Falls back to ``null`` while the socket is down — components should
// render their last-known polling value in that case.

import { useEffect, useRef, useState } from 'react';

const MIN_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;

function buildWsUrl(backendUrl, token) {
  if (!backendUrl) return null;
  let base = backendUrl.replace(/\/$/, '');
  if (base.startsWith('https://')) {
    base = 'wss://' + base.slice('https://'.length);
  } else if (base.startsWith('http://')) {
    base = 'ws://' + base.slice('http://'.length);
  }
  const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${base}/api/ws/live-stats${tokenParam}`;
}

export default function useLiveStats({ backendUrl, token, enabled = true }) {
  const [stats, setStats] = useState(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const backoffRef = useRef(MIN_BACKOFF_MS);
  const reconnectTimerRef = useRef(null);
  const closedByUsRef = useRef(false);

  useEffect(() => {
    if (!enabled || !backendUrl || !token) {
      return undefined;
    }

    closedByUsRef.current = false;

    const connect = () => {
      const url = buildWsUrl(backendUrl, token);
      if (!url) return;
      let ws;
      try {
        ws = new WebSocket(url);
      } catch (_e) {
        // Browser refused (mixed-content / bad URL) — back off and retry.
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        backoffRef.current = MIN_BACKOFF_MS;
        setConnected(true);
      };
      ws.onmessage = (event) => {
        let payload;
        try {
          payload = JSON.parse(event.data);
        } catch (_e) {
          return;
        }
        if (payload && payload.type === 'stats' && payload.data) {
          setStats(payload.data);
        }
        // Heartbeat frames don't carry stats — they're just liveness pings.
      };
      ws.onerror = () => {
        // Let onclose handle the reconnect; calling close() here would race.
      };
      ws.onclose = () => {
        setConnected(false);
        if (closedByUsRef.current) return;
        scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      const delay = backoffRef.current;
      backoffRef.current = Math.min(delay * 2, MAX_BACKOFF_MS);
      reconnectTimerRef.current = setTimeout(connect, delay);
    };

    connect();

    return () => {
      closedByUsRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (_e) {
          // ignore
        }
        wsRef.current = null;
      }
    };
  }, [backendUrl, token, enabled]);

  return { stats, connected };
}

export { buildWsUrl };
